import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
    Briefcase, 
    MapPin, 
    Search, 
    Filter, 
    ChevronRight, 
    Building2,
    Users2,
    Target,
    Rocket
} from 'lucide-react';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import { useGetPublicJobsQuery } from '../store/slices/apiSlice';
import { usePageMetadata } from '../hooks/usePageMetadata';

const Careers = () => {
    usePageMetadata('careers');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedType, setSelectedType] = useState('all');
    const [selectedDept, setSelectedDept] = useState('all');

    const { data: jobsData, isLoading, error } = useGetPublicJobsQuery({
        type: selectedType === 'all' ? undefined : selectedType,
        department: selectedDept === 'all' ? undefined : selectedDept
    });

    const jobs = jobsData?.data || [];

    const filteredJobs = jobs.filter(job => 
        job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.shortDescription.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const departments = ['all', ...new Set(jobs.map(job => job.department).filter(Boolean))];
    const jobTypes = ['all', 'Full-time', 'Part-time', 'Contract', 'Internship'];

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />
            
            {/* Hero Section */}
            <section className="relative bg-blue-900 py-20 px-6 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-900 to-blue-700 opacity-90"></div>
                <div className="absolute top-0 right-0 w-1/3 h-full bg-blue-500 transform skew-x-12 translate-x-1/2 opacity-20"></div>
                
                <div className="container mx-auto max-w-7xl relative z-10 text-center text-white">
                    <h1 className="text-4xl md:text-6xl font-bold mb-6">Join Our Mission</h1>
                    <p className="text-xl text-blue-100 max-w-2xl mx-auto mb-10">
                        Help us build the bridge for the Nigerian diaspora to connect, invest, and grow back home.
                    </p>
                    
                    <div className="flex flex-wrap justify-center gap-8 mt-12">
                        <div className="flex flex-col items-center">
                            <div className="bg-blue-800/50 p-4 rounded-2xl mb-3">
                                <Users2 className="w-8 h-8 text-blue-300" />
                            </div>
                            <span className="font-semibold">Inclusive Culture</span>
                        </div>
                        <div className="flex flex-col items-center">
                            <div className="bg-blue-800/50 p-4 rounded-2xl mb-3">
                                <Target className="w-8 h-8 text-blue-300" />
                            </div>
                            <span className="font-semibold">Goal Oriented</span>
                        </div>
                        <div className="flex flex-col items-center">
                            <div className="bg-blue-800/50 p-4 rounded-2xl mb-3">
                                <Rocket className="w-8 h-8 text-blue-300" />
                            </div>
                            <span className="font-semibold">Fast Growth</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Filter Section */}
            <section className="py-12 px-6">
                <div className="container mx-auto max-w-7xl">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 -mt-24 relative z-20">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input 
                                    type="text" 
                                    placeholder="Search jobs..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                />
                            </div>
                            
                            <div className="relative">
                                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <select 
                                    value={selectedType}
                                    onChange={(e) => setSelectedType(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none transition-all"
                                >
                                    {jobTypes.map(type => (
                                        <option key={type} value={type}>
                                            {type === 'all' ? 'All Types' : type}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="relative">
                                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <select 
                                    value={selectedDept}
                                    onChange={(e) => setSelectedDept(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none transition-all"
                                >
                                    {departments.map(dept => (
                                        <option key={dept} value={dept}>
                                            {dept === 'all' ? 'All Departments' : dept}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex items-center justify-center bg-blue-600 text-white rounded-xl px-6 py-2 shadow-lg shadow-blue-200 border-0">
                                <span className="font-bold">{filteredJobs.length} Open Roles</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Jobs Listing */}
            <section className="pb-24 px-6">
                <div className="container mx-auto max-w-7xl">
                    {isLoading ? (
                        <div className="flex justify-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                        </div>
                    ) : error ? (
                        <div className="text-center py-20 bg-white rounded-2xl border border-gray-100 italic text-gray-500">
                            Failed to load jobs. Please try again later.
                        </div>
                    ) : filteredJobs.length === 0 ? (
                        <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
                             <Briefcase className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                             <h3 className="text-xl font-semibold text-gray-900">No jobs found</h3>
                             <p className="text-gray-500 mt-2">Try adjusting your filters or search terms.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredJobs.map((job) => (
                                <div key={job.id} className="group bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="bg-blue-600 p-3 rounded-xl text-white shadow-md">
                                            <Briefcase className="w-6 h-6" />
                                        </div>
                                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                            job.type === 'Full-time' ? 'bg-green-50 text-green-700' :
                                            job.type === 'Internship' ? 'bg-purple-50 text-purple-700' :
                                            'bg-gray-100 text-gray-700'
                                        }`}>
                                            {job.type}
                                        </span>
                                    </div>
                                    
                                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors mb-2">
                                        {job.title}
                                    </h3>
                                    
                                    <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
                                        <div className="flex items-center gap-1">
                                            <MapPin className="w-4 h-4" />
                                            {job.location}
                                        </div>
                                        {job.department && (
                                            <div className="flex items-center gap-1">
                                                <Building2 className="w-4 h-4" />
                                                {job.department}
                                            </div>
                                        )}
                                    </div>
                                    
                                    <p className="text-gray-600 mb-6 line-clamp-3 text-sm leading-relaxed">
                                        {job.shortDescription}
                                    </p>
                                    
                                    <Link 
                                        to={`/careers/${job.id}`}
                                        className="flex items-center justify-center w-full py-3 border-2 border-blue-600 text-blue-600 font-bold rounded-xl hover:bg-blue-600 hover:text-white transition-all duration-300"
                                    >
                                        View Details
                                        <ChevronRight className="w-4 h-4 ml-2" />
                                    </Link>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default Careers;
